import React, { useEffect, useMemo, useState } from "react";
import { X, Send, Plus, Trash2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function RequestAvailabilityModal({ officials, onClose, onSent, initialEventId = "" }) {
  const [events, setEvents] = useState([]);
  const [eventIds, setEventIds] = useState(initialEventId ? [initialEventId] : []);
  const [selectedOfficialIds, setSelectedOfficialIds] = useState([]);
  const [roleFilter, setRoleFilter] = useState("both"); // both | referee | scorer
  const [extraDates, setExtraDates] = useState([]); // dates additionnelles non liées à un événement
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("compose"); // compose | sent
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/events");
        const open = data.events.filter((e) => !e.closed);
        setEvents(open);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    })();
  }, []);

  // Officiels visibles selon le filtre de rôle (Tous / Arbitres / Marqueurs).
  const visibleOfficials = useMemo(() => {
    if (roleFilter === "both") return officials;
    return officials.filter((o) => Array.isArray(o.roles) && o.roles.includes(roleFilter));
  }, [officials, roleFilter]);

  const toggleAll = () => {
    const visibleIds = visibleOfficials.map((o) => o.id);
    const allVisibleSelected = visibleIds.every((id) => selectedOfficialIds.includes(id));
    if (allVisibleSelected) {
      // Retire les visibles ; garde les autres (sélectionnés dans un autre filtre)
      setSelectedOfficialIds(selectedOfficialIds.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedOfficialIds(Array.from(new Set([...selectedOfficialIds, ...visibleIds])));
    }
  };

  const toggleEvent = (id) => {
    setEventIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Combine all dates from selected events + extras
  const allDates = useMemo(() => {
    const set = new Set();
    eventIds.forEach((eid) => {
      const ev = events.find((e) => e.id === eid);
      if (!ev) return;
      (ev.games || []).forEach((g) => {
        if (g.date) set.add(g.date);
      });
      if (ev.date) set.add(ev.date);
    });
    extraDates.forEach((d) => d && set.add(d));
    return [...set].sort();
  }, [events, eventIds, extraDates]);

  const setExtraDate = (i, val) => setExtraDates(extraDates.map((d, idx) => (idx === i ? val : d)));
  const addExtraDate = () => setExtraDates([...extraDates, ""]);
  const removeExtraDate = (i) => setExtraDates(extraDates.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (eventIds.length === 0) return toast.error("Sélectionne au moins un événement");
    if (selectedOfficialIds.length === 0) return toast.error("Sélectionne au moins un officiel");
    if (allDates.length === 0) return toast.error("Aucune date dans les événements sélectionnés");
    setLoading(true);
    try {
      // Pour chaque événement choisi, on envoie une demande aux officiels
      // sélectionnés avec toutes les dates trouvées dans CET événement.
      const tally = { sent: 0, mocked: 0, failed: 0, no_phone: 0, requested_official_count: 0 };
      for (const eid of eventIds) {
        const ev = events.find((e) => e.id === eid);
        if (!ev) continue;
        const evDates = new Set();
        (ev.games || []).forEach((g) => g.date && evDates.add(g.date));
        if (ev.date) evDates.add(ev.date);
        extraDates.forEach((d) => d && evDates.add(d));
        const datesArr = [...evDates].sort();
        const { data } = await api.post("/availability-requests", {
          event_id: eid,
          official_ids: selectedOfficialIds,
          dates: datesArr,
          message,
        });
        tally.sent += data.sms?.sent || 0;
        tally.mocked += data.sms?.mocked || 0;
        tally.failed += data.sms?.failed || 0;
        tally.no_phone += data.sms?.no_phone || 0;
        tally.requested_official_count = data.official_count || tally.requested_official_count;
      }
      setResult({
        event_count: eventIds.length,
        official_count: tally.requested_official_count,
        sms: tally,
      });
      setStep("sent");
      onSent && onSent(tally);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const withoutPhone = officials.filter((o) => selectedOfficialIds.includes(o.id) && !o.phone).length;

  return (
    <div className="fixed inset-0 z-[90] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[var(--surface)] border border-[var(--border-strong)] rounded-2xl p-6 max-w-3xl w-full shadow-2xl my-8" data-testid="request-availability-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="label">Demande SMS</div>
            <h3 className="font-serif text-3xl leading-tight">
              {step === "sent" ? "Envoyé." : "Demander dispos par SMS"}
            </h3>
          </div>
          <button onClick={onClose} className="text-[var(--muted)] hover:text-black"><X size={20} /></button>
        </div>

        {step === "compose" && (
          <>
            {/* Events (multi-select) */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <label className="label">Événements ({eventIds.length} sélectionné{eventIds.length > 1 ? "s" : ""}) *</label>
                <button
                  onClick={() => setEventIds(eventIds.length === events.length ? [] : events.map((e) => e.id))}
                  className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-black"
                  data-testid="req-toggle-all-events"
                >
                  {eventIds.length === events.length ? "Tout déselectionner" : "Tout sélectionner"}
                </button>
              </div>
              {events.length === 0 ? (
                <p className="font-mono text-[10px] text-[var(--muted)] mt-1">
                  Aucun événement ouvert — crée-le d&apos;abord depuis Événements.
                </p>
              ) : (
                <div className="border border-black max-h-40 overflow-y-auto">
                  {events.map((e) => {
                    const checked = eventIds.includes(e.id);
                    const dateCount = new Set((e.games || []).map((g) => g.date).filter(Boolean)).size || (e.date ? 1 : 0);
                    return (
                      <label
                        key={e.id}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer border-b border-[var(--border)] last:border-0 ${checked ? "bg-[var(--surface-2)]" : ""}`}
                        data-testid={`req-event-${e.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleEvent(e.id)}
                          className="w-4 h-4 border-2 border-black"
                        />
                        <span className="flex-1 text-sm">
                          <b>{e.name}</b>
                          {e.date && <span className="font-mono text-[10px] text-[var(--muted)] ml-2">{e.date}</span>}
                          <span className="font-mono text-[10px] text-[var(--muted)] ml-2">· {dateCount} date{dateCount > 1 ? "s" : ""}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Dates summary */}
            {eventIds.length > 0 && (
              <div className="mb-4 p-3 border-2 border-black bg-[var(--surface-2)]">
                <div className="label mb-1">Dates demandées ({allDates.length})</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {allDates.map((d) => (
                    <span key={d} className="font-mono text-[10px] uppercase tracking-wider tabular px-1.5 py-0.5 border border-black bg-white">
                      {d}
                    </span>
                  ))}
                </div>
                {allDates.length === 0 && (
                  <p className="font-mono text-[10px] text-[var(--muted)] mt-1">
                    Aucune date trouvée dans les événements choisis. Ajoute des parties à l&apos;horaire ou ajoute des dates supplémentaires ci-dessous.
                  </p>
                )}
              </div>
            )}

            {/* Extra dates */}
            <div className="mb-4">
              <label className="label block mb-1.5">Dates supplémentaires (optionnel)</label>
              <div className="space-y-2">
                {extraDates.map((d, i) => (
                  <div key={i} className="flex gap-2">
                    <input type="date" value={d} onChange={(e) => setExtraDate(i, e.target.value)} className="input-box flex-1" data-testid={`req-date-${i}`} />
                    <button onClick={() => removeExtraDate(i)} className="border border-black p-2 hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)]">
                      <Trash2 size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                ))}
                <button onClick={addExtraDate} className="btn-ghost !px-0 inline-flex items-center gap-1" data-testid="req-add-date-btn">
                  <Plus size={12} strokeWidth={2.5} /> Ajouter une date
                </button>
              </div>
            </div>

            {/* Officials */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <label className="label">Officiels à contacter ({selectedOfficialIds.length}/{officials.length})</label>
                <button onClick={toggleAll} className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-black" data-testid="req-toggle-all-btn">
                  {visibleOfficials.length > 0 && visibleOfficials.every((o) => selectedOfficialIds.includes(o.id)) ? "Tout déselectionner (visible)" : "Tout sélectionner (visible)"}
                </button>
              </div>
              <div className="inline-flex border border-black mb-2" role="tablist" aria-label="Filtre par rôle">
                {[
                  { id: "both", label: "Tous", testid: "req-role-both" },
                  { id: "referee", label: "Arbitres", testid: "req-role-referee" },
                  { id: "scorer", label: "Marqueurs", testid: "req-role-scorer" },
                ].map((opt, i) => {
                  const active = roleFilter === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setRoleFilter(opt.id)}
                      className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 ${i > 0 ? "border-l border-black" : ""} ${active ? "bg-black text-white" : "hover:bg-black/5"}`}
                      data-testid={opt.testid}
                      aria-pressed={active}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div className="border border-black max-h-56 overflow-y-auto">
                {visibleOfficials.length === 0 && (
                  <div className="px-3 py-4 font-mono text-[10px] text-[var(--muted)]">Aucun officiel avec ce rôle.</div>
                )}
                {visibleOfficials.map((o) => {
                  const checked = selectedOfficialIds.includes(o.id);
                  const roleLabel = (o.roles || []).map((r) => (r === "referee" ? "Arbitre" : r === "scorer" ? "Marqueur" : r)).join(" · ");
                  return (
                    <label
                      key={o.id}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer border-b border-[var(--border)] last:border-0 ${checked ? "bg-[var(--surface-2)]" : ""}`}
                      data-testid={`req-official-${o.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedOfficialIds([...selectedOfficialIds, o.id]);
                          else setSelectedOfficialIds(selectedOfficialIds.filter((x) => x !== o.id));
                        }}
                        className="w-4 h-4 border-2 border-black"
                      />
                      <span className="flex-1 text-sm">
                        <b>{o.first_name} {o.last_name}</b>
                        <span className="font-mono text-[10px] text-[var(--muted)] ml-2">
                          {roleLabel}
                        </span>
                        <span className="font-mono text-[10px] text-[var(--muted)] ml-2">
                          {o.phone || <span className="italic">— pas de téléphone —</span>}
                        </span>
                      </span>
                      {!o.phone && <span className="badge-dashed !px-1.5">No SMS</span>}
                    </label>
                  );
                })}
              </div>
              {withoutPhone > 0 && (
                <p className="font-mono text-[10px] text-[var(--muted)] mt-1">
                  ⚠ {withoutPhone} officiel(s) sélectionné(s) sans numéro — ils ne recevront pas de SMS mais la demande sera quand même créée pour leur compte.
                </p>
              )}
            </div>

            {/* Message */}
            <div className="mb-5">
              <label className="label block mb-1.5">Message personnalisé (optionnel)</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ex: Plusieurs tournois — confirmez vos dispos avant vendredi SVP."
                className="input-box min-h-20"
                data-testid="req-message-input"
              />
              <p className="font-mono text-[10px] text-[var(--muted)] mt-1">
                Un SMS sera envoyé par événement à chaque officiel. Le SMS contient le nom de l&apos;événement, les dates et un lien vers /officiel/login.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-[var(--border)]">
              <button onClick={onClose} className="btn-secondary">Annuler</button>
              <button onClick={submit} disabled={loading} className="btn-primary" data-testid="req-send-btn">
                <Send size={14} strokeWidth={2.5} />
                {loading ? "Envoi…" : `Envoyer ${eventIds.length > 1 ? `(${eventIds.length} événements × ${selectedOfficialIds.length})` : `(${selectedOfficialIds.length})`}`}
              </button>
            </div>
          </>
        )}

        {step === "sent" && result && (
          <div data-testid="request-sent-summary">
            <div className="border-2 border-black p-4 mb-4 bg-[var(--surface-2)]">
              <div className="flex items-center gap-3">
                <MessageSquare size={24} strokeWidth={2.5} />
                <div>
                  <div className="font-bold text-lg">Demandes envoyées : {result.event_count} événement{result.event_count > 1 ? "s" : ""} × {result.official_count} officiel{result.official_count > 1 ? "s" : ""}.</div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mt-1">
                    Envoyés : {result.sms.sent} · Mock (Twilio non configuré) : {result.sms.mocked} · Échec : {result.sms.failed} · Sans téléphone : {result.sms.no_phone}
                  </p>
                </div>
              </div>
            </div>
            {result.sms.mocked > 0 && (
              <p className="text-xs text-[var(--muted)] border-l-4 border-black pl-3 italic mb-4">
                ℹ Les SMS sont en mode MOCK (Twilio non configuré). Les destinataires ne recevront pas de message réel — ajoute tes clés Twilio dans backend/.env pour activer l&apos;envoi.
              </p>
            )}
            <p className="text-xs text-[var(--muted)] mb-5">
              Les officiels verront la demande dès leur prochaine connexion à <code className="font-mono">/officiel/login</code>. Tu pourras suivre les réponses dans l&apos;onglet &quot;Disponibilités&quot; de chaque événement.
            </p>
            <div className="flex justify-end">
              <button onClick={onClose} className="btn-primary" data-testid="req-done-btn">Fermer</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
